import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { PlatformOptions, CONTENT_FORMATS } from './CampaignForm';

/**
 * A template expands to the note plus its checklist, because the checklist is
 * only half useful as a private reminder — written into the delivery it also
 * tells the brand what was actually done, which is the thing that saves a
 * revision round.
 */
export function expandTemplate(template) {
    const lines = [];
    if (template.note?.trim()) lines.push(template.note.trim());
    if (template.checklist?.length) {
        lines.push(template.checklist.map(item => `✓ ${item}`).join('\n'));
    }
    return lines.join('\n\n');
}

function TemplateForm({ template, onCancel, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState(() => ({
        name: template?.name || '',
        note: template?.note || '',
        platform: template?.platform || '',
        content_format: template?.content_format || '',
        checklist: template?.checklist || [],
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const save = async () => {
        if (!form.name.trim()) { setError(t('templates.nameRequired')); return; }
        if (!form.note.trim() && !form.checklist.length) {
            setError(t('templates.emptyTemplate'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = template
                ? await api.patch(`/api/campaigns/delivery-templates/${template.id}/`, form)
                : await api.post('/api/campaigns/delivery-templates/', form);
            if (res.status === 200 || res.status === 201) onSaved(res.data);
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === 'limit_reached'
                ? t('templates.limitReached')
                : code === 'empty_template'
                    ? t('templates.emptyTemplate')
                    : t('campaigns.saveError'));
        } finally { setSaving(false); }
    };

    return (
        <div className="rounded-xl border border-slate-300 bg-white p-3 space-y-2">
            <input className="input py-1.5 text-sm" value={form.name} maxLength={80}
                placeholder={t('templates.namePlaceholder')}
                onChange={(e) => set('name', e.target.value)} />

            <textarea className="input py-1.5 text-sm min-h-[80px] resize-y" value={form.note} maxLength={1500}
                placeholder={t('templates.notePlaceholder')}
                onChange={(e) => set('note', e.target.value)} />

            <input className="input py-1.5 text-xs" value={form.checklist.join(', ')}
                placeholder={t('templates.checklistPlaceholder')}
                onChange={(e) => set('checklist',
                    e.target.value.split(',').map(x => x.trim()).filter(Boolean))} />

            <div className="flex gap-2">
                <select className="input py-1.5 text-xs w-auto flex-1" value={form.platform}
                    onChange={(e) => set('platform', e.target.value)}>
                    <option value="">{t('campaigns.platform')}</option>
                    <PlatformOptions t={t} />
                </select>
                <select className="input py-1.5 text-xs w-auto flex-1" value={form.content_format}
                    onChange={(e) => set('content_format', e.target.value)}>
                    <option value="">{t('campaigns.format')}</option>
                    {CONTENT_FORMATS.map(f => <option key={f} value={f}>{t(`campaigns.format_${f}`)}</option>)}
                </select>
            </div>

            {error && <p className="text-[11px] text-red-600">{error}</p>}

            <div className="flex gap-2">
                <button type="button" onClick={onCancel} className="btn-secondary flex-1 py-1.5 text-xs">
                    {t('campaigns.cancel')}
                </button>
                <button type="button" onClick={save} disabled={saving}
                    className="btn-primary flex-1 py-1.5 text-xs flex items-center justify-center gap-1.5">
                    {saving && <span className="spinner-sm" />}
                    {t('templates.saveBtn')}
                </button>
            </div>
        </div>
    );
}

/**
 * Template picker for the delivery form.
 *
 * Sits above the note field: one tap fills the note, and `onApply` hands back
 * the template id so the delivery can report which one was used — that count
 * is what orders this list next time, so the template someone actually uses
 * rises to the front instead of being hunted for.
 */
export default function DeliveryTemplates({ currentNote, onApply }) {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [managing, setManaging] = useState(false);
    const [editing, setEditing] = useState(null);      // template object, or 'new'

    const load = () => api.get('/api/campaigns/delivery-templates/')
        .then(res => setTemplates(res.data || []))
        .catch(() => { })
        .finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        try {
            await api.delete(`/api/campaigns/delivery-templates/${id}/`);
            setTemplates(prev => prev.filter(row => row.id !== id));
        } catch (err) { /* surfaced by the interceptor */ }
    };

    const saveCurrentNote = () => setEditing({ note: currentNote, _fromNote: true });

    if (loading) return null;

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold text-slate-500 flex-shrink-0">
                    {t('templates.label')}
                </span>

                {templates.map(template => (
                    <button key={template.id} type="button"
                        onClick={() => onApply(expandTemplate(template), template.id)}
                        title={template.note}
                        className="px-2 py-1 rounded-lg bg-white text-slate-600 border border-slate-200 hover:border-primary-300 hover:text-primary-700 transition text-[11px] font-semibold max-w-[160px] truncate">
                        {template.name}
                        {template.use_count > 0 && (
                            <span className="text-slate-300 ml-1">·{template.use_count}</span>
                        )}
                    </button>
                ))}

                {/* Saving the note you just wrote is the moment a template
                    actually gets created, so the button lives right here. */}
                {currentNote?.trim() && !editing && (
                    <button type="button" onClick={saveCurrentNote}
                        className="px-2 py-1 rounded-lg bg-primary-50 text-primary-700 border border-primary-100 hover:bg-primary-100 transition text-[11px] font-semibold">
                        + {t('templates.saveThis')}
                    </button>
                )}

                <button type="button" onClick={() => setManaging(!managing)}
                    className="text-[11px] font-semibold text-slate-400 hover:text-slate-700 ml-auto">
                    {managing ? t('templates.done') : t('templates.manage')}
                </button>
            </div>

            {templates.length === 0 && !managing && !editing && (
                <p className="text-[11px] text-slate-400 leading-relaxed">{t('templates.emptyHint')}</p>
            )}

            {editing && (
                <TemplateForm
                    template={editing._fromNote ? null : editing}
                    onCancel={() => setEditing(null)}
                    onSaved={(saved) => {
                        setTemplates(prev => {
                            const exists = prev.some(row => row.id === saved.id);
                            return exists ? prev.map(row => (row.id === saved.id ? saved : row)) : [...prev, saved];
                        });
                        setEditing(null);
                    }}
                />
            )}

            {managing && !editing && (
                <div className="space-y-1.5">
                    {templates.map(template => (
                        <div key={template.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-slate-700 truncate">{template.name}</p>
                                <p className="text-[10px] text-slate-400 truncate">{template.note}</p>
                            </div>
                            <button type="button" onClick={() => setEditing(template)}
                                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-1">
                                {t('templates.edit')}
                            </button>
                            <button type="button" onClick={() => remove(template.id)}
                                className="text-red-300 hover:text-red-600 px-1">×</button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setEditing({ _fromNote: true, note: '' })}
                        className="btn-secondary text-[11px] py-1.5 px-3 w-full">
                        + {t('templates.newTemplate')}
                    </button>
                </div>
            )}
        </div>
    );
}
